import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState, fmtMoney } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Upload } from "lucide-react";

export default function Reconciliation() {
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadList = () => api.get("/reconciliations").then((r) => setList(r.data));
  useEffect(() => { loadList(); }, []);
  const open = async (id) => { const r = await api.get(`/reconciliations/${id}`); setDetail(r.data); };
  const resolve = async (row, action) => {
    const note = prompt(`Resolution note for ${row.folio}/${row.scheme}`) || action;
    await api.post(`/reconciliations/${detail.id}/resolve`, { folio: row.folio, scheme: row.scheme, action, note });
    toast.success("Resolution recorded"); open(detail.id); loadList();
  };

  if (!list) return <Spinner />;

  if (detail) {
    const r = detail.results;
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => setDetail(null)} className="mb-3 gap-1" data-testid="recon-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <PageHeader overline={`ARN ${detail.arn || "—"} · valuation ${detail.valuation_date}`} title={detail.name}
          subtitle={`Tolerances: ${detail.tolerance_units} units / ${detail.tolerance_value_pct}% value. Rules are provisional until Barjeel confirms them.`} testid="recon-detail-header" />

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <Stat label="Matched" v={r.matched.length} cls="text-emerald-600" testid="recon-matched" />
          <Stat label="Missing (external)" v={r.missing_external.length} cls="text-amber-600" />
          <Stat label="Missing (internal)" v={r.missing_internal.length} cls="text-amber-600" />
          <Stat label="Unit diffs" v={r.unit_diffs.length} cls="text-red-600" />
          <Stat label="Value diffs" v={r.value_diffs.length} cls="text-red-600" />
        </div>

        <ReconTable title="Matched" rows={r.matched} />
        <ExceptionTable title="Unit differences" rows={r.unit_diffs} onResolve={resolve} />
        <ExceptionTable title="Value differences" rows={r.value_diffs} onResolve={resolve} />
        <SimpleTable title="Missing in external statement (present internally)" rows={r.missing_external} />
        <SimpleTable title="Missing internally (present in external statement)" rows={r.missing_internal} />

        {detail.resolutions?.length > 0 && (
          <Card className="p-5 mt-4">
            <h3 className="font-semibold mb-3">Resolution actions (recorded)</h3>
            {detail.resolutions.map((x, i) => <div key={i} className="text-xs border-l-2 border-border pl-2 py-1"><span className="font-medium">{x.folio}/{x.scheme}</span> · {x.action} · {x.note} · {x.by} · {new Date(x.ts).toLocaleString()}</div>)}
          </Card>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Operations" title="ARN-wise AUM Reconciliation" subtitle="Reconcile internal holdings against an uploaded external statement at a common valuation date. Rules are provisional until Barjeel confirms them." testid="recon-header"
        actions={<>
          <input type="file" accept=".csv,.xlsx" id="reconfile" className="hidden" data-testid="recon-file" onChange={(e) => {
            const file = e.target.files[0]; if (!file) return;
            const name = prompt("Reconciliation name", "External Statement Reconciliation"); if (!name) { e.target.value = ""; return; }
            const arn = prompt("ARN to match internal holdings (optional, e.g. ARN-12345)", "") || "";
            const reader = new FileReader();
            reader.onload = async () => {
              try { await api.post("/reconciliations", { name, arn, external_base64: reader.result, file_type: file.name.endsWith(".xlsx") ? "xlsx" : "csv" }); toast.success("Reconciliation created"); loadList(); }
              catch (err) { toast.error(err?.response?.data?.detail || "Failed to create reconciliation"); }
            };
            reader.readAsDataURL(file); e.target.value = "";
          }} />
          <Button className="gap-1" onClick={() => document.getElementById("reconfile").click()} data-testid="new-recon-btn"><Upload className="h-4 w-4" /> New Reconciliation</Button>
        </>} />
      <Card className="p-5">
        {list.length === 0 ? <EmptyState title="No reconciliations" /> : (
          <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>ARN</TableHead><TableHead>Valuation</TableHead><TableHead>Matched</TableHead><TableHead>Exceptions</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{list.map((x) => (
              <TableRow key={x.id} data-testid={`recon-row-${x.id}`}>
                <TableCell className="font-medium">{x.name}</TableCell><TableCell className="font-num text-xs">{x.arn}</TableCell><TableCell className="font-num text-xs">{x.valuation_date}</TableCell>
                <TableCell className="font-num text-emerald-600">{x.summary.matched}</TableCell><TableCell className="font-num text-red-600">{x.summary.exceptions}</TableCell>
                <TableCell><StatusBadge status={x.status} /></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => open(x.id)} data-testid={`open-recon-${x.id}`}>Open</Button></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, v, cls = "", testid }) { return <Card className="p-4" data-testid={testid}><div className="overline-label text-muted-foreground">{label}</div><div className={`font-num font-bold text-2xl ${cls}`}>{v}</div></Card>; }

function ReconTable({ title, rows }) {
  if (!rows.length) return null;
  return (
    <Card className="p-5 mb-4">
      <h3 className="font-semibold mb-3">{title}</h3>
      <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Int. Units</TableHead><TableHead className="text-right">Ext. Units</TableHead><TableHead className="text-right">Int. Value</TableHead><TableHead className="text-right">Ext. Value</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => (
          <TableRow key={i}><TableCell className="font-num text-xs">{r.folio}</TableCell><TableCell>{r.scheme}</TableCell>
            <TableCell className="text-right font-num">{r.internal_units}</TableCell><TableCell className="text-right font-num">{r.external_units}</TableCell>
            <TableCell className="text-right font-num">{fmtMoney(r.internal_value, r.currency)}</TableCell><TableCell className="text-right font-num">{fmtMoney(r.external_value, r.currency)}</TableCell></TableRow>
        ))}</TableBody>
      </Table></div>
    </Card>
  );
}

function ExceptionTable({ title, rows, onResolve }) {
  if (!rows.length) return null;
  return (
    <Card className="p-5 mb-4 border-red-200">
      <h3 className="font-semibold mb-3 text-red-700">{title}</h3>
      <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Unit diff</TableHead><TableHead className="text-right">Value diff</TableHead><TableHead className="text-right">Value diff %</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => (
          <TableRow key={i} data-testid={`exception-${r.folio}`}><TableCell className="font-num text-xs">{r.folio}</TableCell><TableCell>{r.scheme}</TableCell>
            <TableCell className="text-right font-num text-red-600">{r.unit_diff}</TableCell><TableCell className="text-right font-num text-red-600">{fmtMoney(r.value_diff, r.currency)}</TableCell>
            <TableCell className="text-right font-num">{r.value_diff_pct}%</TableCell>
            <TableCell className="text-right whitespace-nowrap">
              <Button size="sm" variant="ghost" onClick={() => onResolve(r, "accepted")} data-testid={`resolve-accept-${r.folio}`}>Accept</Button>
              <Button size="sm" variant="ghost" onClick={() => onResolve(r, "flagged")}>Flag</Button>
            </TableCell></TableRow>
        ))}</TableBody>
      </Table></div>
    </Card>
  );
}

function SimpleTable({ title, rows }) {
  if (!rows.length) return null;
  return (
    <Card className="p-5 mb-4">
      <h3 className="font-semibold mb-3 text-amber-700">{title}</h3>
      <Table><TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Units</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => <TableRow key={i}><TableCell className="font-num text-xs">{r.folio}</TableCell><TableCell>{r.scheme}</TableCell><TableCell className="text-right font-num">{r.units}</TableCell><TableCell className="text-right font-num">{fmtMoney(r.value, r.currency)}</TableCell></TableRow>)}</TableBody>
      </Table>
    </Card>
  );
}
